import React, { useCallback, useEffect, useState } from 'react'
import { Edit2, Loader2, Plus, Trash2, UserCircle, X } from 'lucide-react'
import LoadingSpinner from '@/components/ui/LoadingSpinner'
import { api } from '@/lib/api'

interface AgencyCounselor {
  id: string
  userId: string
  firstName: string
  lastName: string
  email: string
  phoneNumber: string
  specializations: string[]
  experienceYears: number
  bio: string
  isActive: boolean
}

interface FormState {
  name: string
  phoneNumber: string
  email: string
  specializations: string
  experience: string
  bio: string
}

const EMPTY_FORM: FormState = {
  name: '',
  phoneNumber: '',
  email: '',
  specializations: '',
  experience: '',
  bio: '',
}

/**
 * The counselor-roster page scoped to a single agency's own admin.
 * Deliberately a separate, much smaller page than CounselorManagement.tsx —
 * that one talks to /admin/counselors* with the full admin console's
 * capabilities (reassigning agencies, approvals, etc.), none of which an
 * agency admin should have. This page uses the same create/update endpoints
 * (now agency-scoped server-side — see admin.service.ts) but exposes only
 * what an agency should be able to do with its own counselors: add, edit,
 * remove. Delete goes through /profile/counselor/:userId, the one endpoint
 * that actually supports it.
 */
const AgencyCounselors: React.FC = () => {
  const [counselors, setCounselors] = useState<AgencyCounselor[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  const [showModal, setShowModal] = useState(false)
  const [editing, setEditing] = useState<AgencyCounselor | null>(null)
  const [form, setForm] = useState<FormState>(EMPTY_FORM)
  const [submitting, setSubmitting] = useState(false)
  const [formError, setFormError] = useState('')

  const [deletingId, setDeletingId] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const res = await api.get<{ data: AgencyCounselor[] }>('/admin/counselors', { limit: 100 })
      setCounselors(res.data || [])
    } catch (err: any) {
      setError(err?.message || 'Failed to load counselors')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const openCreate = () => {
    setEditing(null)
    setForm(EMPTY_FORM)
    setFormError('')
    setShowModal(true)
  }

  const openEdit = (c: AgencyCounselor) => {
    setEditing(c)
    setForm({
      name: `${c.firstName} ${c.lastName}`.trim(),
      phoneNumber: c.phoneNumber || '',
      email: c.email || '',
      specializations: (c.specializations || []).join(', '),
      experience: c.experienceYears ? String(c.experienceYears) : '',
      bio: c.bio || '',
    })
    setFormError('')
    setShowModal(true)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSubmitting(true)
    setFormError('')

    const payload = {
      name: form.name.trim(),
      phoneNumber: form.phoneNumber.trim(),
      email: form.email.trim() || undefined,
      specializations: form.specializations
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean),
      experience: form.experience ? Number(form.experience) : undefined,
      bio: form.bio.trim() || undefined,
    }

    try {
      if (editing) {
        await api.patch(`/admin/counselors/${editing.id}`, payload)
      } else {
        await api.post('/admin/counselors', payload)
      }
      setShowModal(false)
      await load()
    } catch (err: any) {
      setFormError(err?.message || 'Failed to save counselor')
    } finally {
      setSubmitting(false)
    }
  }

  const handleDelete = async (c: AgencyCounselor) => {
    if (!window.confirm(`Remove ${c.firstName} ${c.lastName}? This cannot be undone.`)) return
    setDeletingId(c.userId)
    try {
      await api.delete(`/profile/counselor/${c.userId}`)
      await load()
    } catch (err: any) {
      setError(err?.message || 'Failed to remove counselor')
    } finally {
      setDeletingId(null)
    }
  }

  return (
    <div className="p-4 sm:p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">My Counselors</h1>
          <p className="text-sm text-gray-500 mt-1">
            Add, edit or remove the counselors working under your agency.
          </p>
        </div>
        <button
          onClick={openCreate}
          className="inline-flex items-center gap-1.5 px-3 py-2 bg-primary text-white text-sm font-medium rounded-md hover:bg-primary/90"
        >
          <Plus className="h-4 w-4" />
          Add Counselor
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-3 mb-4 text-sm text-red-800">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-16">
          <LoadingSpinner size="lg" />
        </div>
      ) : counselors.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-lg border border-gray-200">
          <UserCircle className="h-10 w-10 text-gray-300 mx-auto mb-2" />
          <p className="text-sm text-gray-500">No counselors yet — add your first one.</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contact</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Specializations</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Experience</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {counselors.map((c) => (
                <tr key={c.id}>
                  <td className="px-4 py-4 text-sm font-medium text-gray-900 whitespace-nowrap">
                    {c.firstName} {c.lastName}
                  </td>
                  <td className="px-4 py-4 text-sm text-gray-600">
                    <div>{c.email || 'N/A'}</div>
                    <div className="text-xs text-gray-400">{c.phoneNumber}</div>
                  </td>
                  <td className="px-4 py-4 text-sm text-gray-600">
                    {(c.specializations || []).join(', ') || 'N/A'}
                  </td>
                  <td className="px-4 py-4 text-sm text-gray-600 whitespace-nowrap">
                    {c.experienceYears ? `${c.experienceYears} yrs` : 'N/A'}
                  </td>
                  <td className="px-4 py-4 text-right whitespace-nowrap">
                    <button
                      onClick={() => openEdit(c)}
                      className="text-gray-500 hover:text-primary mr-3"
                      aria-label="Edit"
                    >
                      <Edit2 className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(c)}
                      disabled={deletingId === c.userId}
                      className="text-gray-500 hover:text-red-600 disabled:opacity-50"
                      aria-label="Remove"
                    >
                      {deletingId === c.userId ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Trash2 className="h-4 w-4" />
                      )}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-semibold text-gray-900">
                  {editing ? 'Edit Counselor' : 'Add Counselor'}
                </h2>
                <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600">
                  <X className="h-5 w-5" />
                </button>
              </div>

              {formError && (
                <div className="bg-red-50 border border-red-200 rounded-md p-3 mb-4 text-sm text-red-800">
                  {formError}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
                  <input
                    type="text"
                    required
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Mobile Number *</label>
                  <input
                    type="tel"
                    required
                    disabled={!!editing}
                    value={form.phoneNumber}
                    onChange={(e) => setForm({ ...form, phoneNumber: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary disabled:bg-gray-50 disabled:text-gray-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Specializations</label>
                  <input
                    type="text"
                    placeholder="e.g. Study Abroad, Test Prep"
                    value={form.specializations}
                    onChange={(e) => setForm({ ...form, specializations: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                  <p className="mt-1 text-xs text-gray-500">Comma-separated.</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Years of Experience</label>
                  <input
                    type="number"
                    min={0}
                    value={form.experience}
                    onChange={(e) => setForm({ ...form, experience: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Bio</label>
                  <textarea
                    rows={3}
                    value={form.bio}
                    onChange={(e) => setForm({ ...form, bio: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90 disabled:opacity-50"
                  >
                    {submitting ? 'Saving...' : editing ? 'Save Changes' : 'Add Counselor'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default AgencyCounselors
